import t from "@ext/localization/locale/translate";
import { useIsStorageConnected } from "@ext/storage/logic/utils/useStorage";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui-kit/AlertDialog";
import { Loader } from "@ui-kit/Loader";
import { Prose } from "@ui-kit/Prose";
import { useState } from "react";

export type ReformatCatalogConfirmProps = {
	hasUnpublished: boolean;
	onCancel: () => void;
	onConfirm: () => Promise<void>;
	onGoToPublish: () => void;
};

/**
 * The rewritten markup lies in lines colleagues may be editing: their unpublished edits there meet it as
 * conflicts on their next sync. A catalog with a repository therefore gets the order that avoids them.
 */
const TeamSteps = () => {
	const prefix = "forms.catalog-extended-edit-props.props.reformat.confirm.team";

	return (
		<>
			{/* The lead-in belongs to the list; the ui-kit preset also indents a list past the text. */}
			<p className="mb-1">{t(`${prefix}.conflicts`)}</p>
			<ol className="mt-1 ml-0 marker:text-muted">
				<li>{t(`${prefix}.publish`)}</li>
				<li>{t(`${prefix}.reformat`)}</li>
				<li>{t(`${prefix}.sync`)}</li>
			</ol>
		</>
	);
};

/**
 * Unpublished edits get a recommendation, not a refusal: `Reformat` still does it right now. The window
 * stays until the report replaces it, so a large catalog is not half a minute of nothing.
 */
const ReformatCatalogConfirm = ({
	hasUnpublished,
	onCancel,
	onConfirm,
	onGoToPublish,
}: ReformatCatalogConfirmProps) => {
	const prefix = "forms.catalog-extended-edit-props.props.reformat.confirm";
	const [isRunning, setIsRunning] = useState(false);
	const isStorageConnected = useIsStorageConnected();

	const confirm = () => {
		setIsRunning(true);
		void onConfirm();
	};

	return (
		<AlertDialog open>
			<AlertDialogContent status="warning">
				{/* Lists read only left-aligned: the ui-kit centres the text below `lg`, and a desktop window is
				    often that narrow -- the same override as `AlertProgressConfirm`. */}
				<AlertDialogHeader className="grid min-w-0 grid-cols-[0_minmax(0,1fr)] items-start gap-y-4">
					<AlertDialogTitle className="col-start-2 mb-0 text-left">{t(`${prefix}.title`)}</AlertDialogTitle>
					<AlertDialogDescription asChild className="col-start-2 text-left">
						<Prose className="prose-sm text-muted">
							<p>{t(`${prefix}.description`)}</p>
							{hasUnpublished && <p>{t(`${prefix}.unpublished`)}</p>}
							{isStorageConnected && <TeamSteps />}
						</Prose>
					</AlertDialogDescription>
				</AlertDialogHeader>
				{/* Below `lg` the dialog is 360px wide: the buttons stack full width, main one on top, as the
				    ui-kit does on a phone. The dialog is controlled, so its actions do not close it. */}
				<AlertDialogFooter className="min-w-0 gap-3 sm:flex-col-reverse sm:space-x-0 lg:flex-row lg:flex-wrap lg:justify-end">
					<AlertDialogCancel
						className="whitespace-nowrap"
						disabled={isRunning}
						onClick={onCancel}
						variant="outline"
					>
						{t("cancel")}
					</AlertDialogCancel>
					{/* Rewriting every file is risky but loses nothing -- warning, not danger. With
					    unpublished edits the safe way out is the primary one: publish them first. */}
					<AlertDialogAction
						className="whitespace-nowrap"
						disabled={isRunning}
						onClick={confirm}
						status="warning"
						variant={hasUnpublished ? "outline" : "primary"}
					>
						{isRunning && <Loader className="px-0" size="md" />}
						{t(`${prefix}.action`)}
					</AlertDialogAction>
					{hasUnpublished && (
						<AlertDialogAction
							className="whitespace-nowrap"
							disabled={isRunning}
							onClick={onGoToPublish}
							status="default"
							variant="primary"
						>
							{t("forms.catalog-extended-edit-props.props.reformat.publish")}
						</AlertDialogAction>
					)}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default ReformatCatalogConfirm;
